import { breadcrumbJsonLd, faqJsonLd } from "@amtana/browser";
import type { SeoJsonLdBlock } from "@amtana/browser";
import { PRODUCT_NAME } from "@amtana/i18n/catalog";
import type { PostFile } from "virtual:blog-posts";

import { SITE_URL } from "./model/site-url.ts";

// A post declares what it shows (ADR-029 point 3): `BlogPosting` with an absolute image, a publisher,
// its language and its dates, a `BreadcrumbList`, and `FAQPage` only when the post has a visible FAQ.

const DEFAULT_IMAGE = "/share.png";
const LOGO = "/brand/zaistev-icon-colour.png";

const absolute = (path: string): string => (path.startsWith("https://") ? path : `${SITE_URL}${path}`);

export function postJsonLd(input: {
  readonly file: PostFile;
  readonly language: string;
  /** This page's own address, language prefix included. */
  readonly address: string;
  /** The index's address and name, in the page's language. */
  readonly blog: { readonly path: string; readonly name: string };
}): readonly SeoJsonLdBlock[] {
  const { file, language, address, blog } = input;
  const dates = file.publishedAt
    ? { datePublished: file.publishedAt, dateModified: file.updatedAt ?? file.publishedAt }
    : {};
  const posting: SeoJsonLdBlock = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: file.title,
    description: file.description,
    image: absolute(file.image ?? DEFAULT_IMAGE),
    inLanguage: language,
    ...dates,
    mainEntityOfPage: absolute(address),
    author: { "@type": "Organization", name: file.authorName },
    publisher: {
      "@type": "Organization",
      name: PRODUCT_NAME,
      logo: { "@type": "ImageObject", url: absolute(LOGO) },
    },
  };
  const breadcrumb = breadcrumbJsonLd({
    siteUrl: SITE_URL,
    items: [blog, { name: file.title, path: address }],
  });
  return [posting, breadcrumb, ...(file.faq?.length ? [faqJsonLd(file.faq)] : [])];
}
